import { type FC } from 'react'

import { F0Box, F0Button, F0Icon, F0Text } from '@factorialco/f0-react'
import { Delete, Shield } from '@factorialco/f0-react/icons/app'

import formatCurrency from 'modules/core/lib/formatCurrency'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'
import { DEVICE_CATALOG_MAX_QUANTITY } from 'modules/it_management/hooks/device_catalog/constants'
import type { CartItem, OfferType } from 'modules/it_management/hooks/device_catalog/types'
import { NumberCounter } from 'modules/it_management/components/device_catalog/NumberCounter'

export type CartItemRowVariant = 'cart' | 'checkout'

export type CartItemRowProps = {
  item: CartItem
  onUpdateQuantity: (variantId: string, offerId: string, quantity: number) => void
  onRemove: (variantId: string, offerId: string) => void
  /** 'cart' = sidepanel layout (stacked). 'checkout' = order plan layout (horizontal row). */
  variant?: CartItemRowVariant
}

function formatPrice(amount: number, isMonthly = false): string {
  const formatted = formatCurrency(toCents(amount))
  return isMonthly ? `${formatted}/m` : formatted
}

function getOfferModelLabel(offerType: OfferType, offerPeriod: number | null): string {
  switch (offerType) {
    case 'onetime':
      return i18n.t('it_management.device_catalog.cart.offer_buy')
    case 'renting':
      return i18n.t('it_management.device_catalog.cart.offer_rent_months', {
        months: offerPeriod ?? 36,
      })
    case 'credit':
      return i18n.t('it_management.device_catalog.cart.offer_credit_months', {
        months: offerPeriod ?? 12,
      })
    default:
      return i18n.t('it_management.device_catalog.cart.offer_buy')
  }
}

export const CartItemRow: FC<CartItemRowProps> = ({
  item,
  onUpdateQuantity,
  onRemove,
  variant = 'cart',
}) => {
  const hasZeroTouch = item.addons.some(
    (addon) => addon.id === 'zero-touch' || addon.id === 'it-subscription'
  )
  const offerLabel = getOfferModelLabel(item.offerType, item.offerPeriod)
  const lineTotal = item.price * item.quantity

  if (variant === 'checkout') {
    const isMonthly = item.offerType !== 'onetime'
    return (
      <F0Box display="flex" flexDirection="row" alignItems="center" paddingY="sm">
        <div className="flex min-w-0 flex-1 flex-row items-center gap-3">
          <F0Box width="12" height="12" display="flex" alignItems="center" justifyContent="center">
            <img
              src={item.image}
              alt={item.name}
              className="object-contain max-w-full max-h-full"
            />
          </F0Box>
          <F0Box display="flex" flexDirection="column" gap="xs">
            <F0Text variant="label" content={item.name} />
            <F0Text variant="description" content={offerLabel} />
          </F0Box>
        </div>
        <div className="flex shrink-0 flex-row flex-nowrap items-center justify-end gap-3">
          <F0Text variant="description" content={formatPrice(item.price, isMonthly)} />
          <NumberCounter
            value={item.quantity}
            onChange={(newQty) => onUpdateQuantity(item.variantId, item.offerId, newQty)}
            max={
              item.stock != null
                ? Math.min(item.stock, DEVICE_CATALOG_MAX_QUANTITY)
                : DEVICE_CATALOG_MAX_QUANTITY
            }
            decreaseLabel={i18n.t('it_management.device_catalog.cart.quantity_decrease')}
            increaseLabel={i18n.t('it_management.device_catalog.cart.quantity_increase')}
          />
        </div>
        <div className="flex w-[200px] shrink-0 flex-row items-center justify-end gap-3">
          <F0Text variant="label" content={formatPrice(lineTotal, isMonthly)} />
          <F0Button
            hideLabel
            variant="ghost"
            icon={Delete}
            onClick={() => onRemove(item.variantId, item.offerId)}
            aria-label={i18n.t('it_management.device_catalog.cart.remove_item')}
          />
        </div>
      </F0Box>
    )
  }

  return (
    <F0Box
      display="flex"
      flexDirection="column"
      border="default"
      borderColor="secondary"
      borderRadius="md"
      overflow="hidden"
    >
      <F0Box display="flex" flexDirection="row" alignItems="center" gap="lg" padding="md">
        <F0Box width="20" height="20" display="flex" alignItems="center" justifyContent="center">
          <img src={item.image} alt={item.name} className="object-contain max-w-full max-h-full" />
        </F0Box>

        <F0Box height="full" display="flex" flexDirection="column" justifyContent="center" gap="md">
          <F0Text variant="label" content={item.name} />
          <F0Text variant="description" content={offerLabel} />
          <F0Text variant="body" content={formatPrice(item.price, item.offerType !== 'onetime')} />
          {hasZeroTouch && (
            <F0Box display="flex" flexDirection="row" gap="xs" alignItems="center">
              <F0Icon icon={Shield} size="sm" color="default" />
              <F0Text
                variant="description"
                content={i18n.t('it_management.device_catalog.cart.zero_touch_badge')}
              />
            </F0Box>
          )}
        </F0Box>
      </F0Box>

      <div className="h-[1px] w-full bg-f1-border-secondary" role="separator" />

      <F0Box
        display="flex"
        flexDirection="row"
        justifyContent="between"
        alignItems="center"
        paddingX="md"
        paddingTop="md"
        paddingBottom="md"
      >
        <NumberCounter
          value={item.quantity}
          onChange={(newQty) => onUpdateQuantity(item.variantId, item.offerId, newQty)}
          max={
            item.stock != null
              ? Math.min(item.stock, DEVICE_CATALOG_MAX_QUANTITY)
              : DEVICE_CATALOG_MAX_QUANTITY
          }
          decreaseLabel={i18n.t('it_management.device_catalog.cart.quantity_decrease')}
          increaseLabel={i18n.t('it_management.device_catalog.cart.quantity_increase')}
        />
        <F0Button
          hideLabel
          variant="ghost"
          icon={Delete}
          onClick={() => onRemove(item.variantId, item.offerId)}
          aria-label={i18n.t('it_management.device_catalog.cart.remove_item')}
        />
      </F0Box>
    </F0Box>
  )
}
